import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import AppRoutes from "./routes/routes";
import { postMessage } from "./lib/post-message";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "./redux/store";
import Toaster from "./components/template/Toaster/toaster";
import { hideToaster } from "./redux/slices/toasterSlice";
import { checkToken } from "./redux/slices/authSlice";
import { ToastContainer } from "react-toastify";
import { updateIndexDBSettings, getIndexDBSettings } from "./db";
import { jwtDecode } from "jwt-decode";
import { turnOfflineModeOn, isOfflineMode } from "./shared/offlineManager";

// Simple migration function to store userId in IndexedDB
async function migrateUserIdToIndexedDB(userId: string | null) {
  if (!userId) return;
  
  try {
    // Check if userId is already stored
    const existingUserId = await getIndexDBSettings("userId");
    
    if (existingUserId === userId) {
      // Already up to date
      return;
    }
    
    // Store userId in IndexedDB
    await updateIndexDBSettings("userId", userId);
    console.log("✅ Migrated userId to IndexedDB:", userId);
  } catch (error) {
    console.error("Error migrating userId to IndexedDB:", error);
  }
}

// Simple migration function to store tenant in IndexedDB
async function migrateTenantToIndexedDB(tenant: string | null) {
  if (!tenant) return;
  
  try {
    // Check if tenant is already stored
    const existingTenant = await getIndexDBSettings("tenant");
    
    if (existingTenant === tenant) {
      // Already up to date
      return;
    }
    
    // Store tenant in IndexedDB
    await updateIndexDBSettings("tenant", tenant);
    console.log("✅ Migrated tenant to IndexedDB:", tenant);
  } catch (error) {
    console.error("Error migrating tenant to IndexedDB:", error);
  }
}

// Simple migration function to store tenantKey in IndexedDB
// If tenantKey is not in Redux, try to extract it from the token
async function migrateTenantKeyToIndexedDB(tenantKey: string | null, token: string | null) {
  let keyToStore = tenantKey;
  
  // If tenantKey is not in Redux but we have a token, extract it from token
  if (!keyToStore && token) {
    try {
      const decoded: any = jwtDecode(token);
      if (decoded.TenantKey) {
        keyToStore = decoded.TenantKey;
      }
    } catch (error) {
      console.error("Failed to decode token for tenantKey:", error);
    }
  }
  
  if (!keyToStore) return;
  
  try {
    // Check if tenantKey is already stored
    const existingTenantKey = await getIndexDBSettings("tenantKey");
    
    if (existingTenantKey === keyToStore) {
      // Already up to date
      return;
    }
    
    // Store tenantKey in IndexedDB
    await updateIndexDBSettings("tenantKey", keyToStore);
    console.log("✅ Migrated tenantKey to IndexedDB:", keyToStore);
  } catch (error) {
    console.error("Error migrating tenantKey to IndexedDB:", error);
  }
}

const App = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { message, type } = useSelector((state: RootState) => state.toaster);
  const auth = useSelector((state: RootState) => state.auth);

  const handleToasterClose = () => {
    dispatch(hideToaster());
  };

  // Set user context and start page transaction
  useEffect(() => {
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const pageName =
      pathSegments.length > 0 ? pathSegments[pathSegments.length - 1] : "Home";

    // Set user context in Sentry
    if (auth?.userId || auth?.tenant) {
      Sentry.setUser({
        id: auth.userId || "",
        email: auth.userName || "",
        username: auth.userName || "",
      });

      // Set custom context for user info
      Sentry.setContext("user_info", {
        userId: auth.userId,
        tenant: auth.tenant,
        username: auth.userName,
      });

      // Set tags for better filtering
      Sentry.setTag("tenant", auth.tenant || "");
      Sentry.setTag("userId", auth.userId || "");
      Sentry.setTag("currentPage", pageName);

      // Migration: Store userId, tenant, and tenantKey in IndexedDB for service worker access
      migrateUserIdToIndexedDB(auth.userId);
      migrateTenantToIndexedDB(auth.tenant);
      migrateTenantKeyToIndexedDB(auth.tenantKey, auth.token);
    }

    // Add breadcrumb for page navigation
    Sentry.addBreadcrumb({
      message: `Navigated to ${pageName}`,
      category: "navigation",
      level: "info",
      data: {
        path: window.location.pathname,
        page: pageName,
      },
    });

    const handleLoad = () => {
      // Add breadcrumb for completed load
      Sentry.addBreadcrumb({
        message: `Page ${pageName} loaded successfully`,
        category: "performance",
        level: "info",
        data: {
          loadTime: performance.now(),
          page: pageName,
        },
      });
    };

    window.addEventListener("load", handleLoad);

    return () => {
      window.removeEventListener("load", handleLoad);
      
    };
  }, [auth]);

  // Token checking with error handling
  useEffect(() => {
    const handleCheckToken = async () => {
      await dispatch(checkToken());
      try {
        console.log("Checking token");

        // Add breadcrumb for token check
        Sentry.addBreadcrumb({
          message: "Checking authentication token",
          category: "auth",
          level: "info",
          data: {
            userId: auth?.userId || "anonymous",
            tenant: auth?.tenant || "unknown",
          },
        });

        await dispatch(checkToken());

        // Success breadcrumb
        Sentry.addBreadcrumb({
          message: "Token check completed successfully",
          category: "auth",
          level: "info",
        });
      } catch (error) {
        // Set user context for error reporting
        if (auth?.userId || auth?.tenant) {
          Sentry.setUser({
            id: auth.userId || "",
            email: auth.userName || "",
            username: auth.userName || "",
          });

          Sentry.setContext("user_info", {
            userId: auth.userId,
            tenant: auth.tenant,
            username: auth.userName,
          });
        }

        // Capture the error
        Sentry.captureException(error, {
          tags: {
            source: "token_check",
            userId: auth?.userId || "",
            tenant: auth?.tenant || "",
          },
          contexts: {
            auth_check: {
              action: "periodic_token_check",
              timestamp: new Date().toISOString(),
            },
          },
        });

        console.error("Token check failed:", error);
      }
    };

    handleCheckToken();

    const interval = setInterval(() => {
      console.log("Checking token in interval");
      handleCheckToken();
    }, 600000); // 10 minutes

    return () => clearInterval(interval);
  }, [dispatch, auth]);

  // Service worker and network status monitoring
  useEffect(() => {
    navigator.serviceWorker.ready
      .then(() => {
        console.log("Service worker is ready. Syncing queue...");

        // Add breadcrumb for service worker ready
        Sentry.addBreadcrumb({
          message: "Service worker ready, syncing queue",
          category: "sw",
          level: "info",
          data: {
            userId: auth?.userId || "anonymous",
            tenant: auth?.tenant || "unknown",
          },
        });

        postMessage({
          type: "SYNC_QUEUE",
          payload: {
            message: "Sync queue",
          },
        });
      })
      .catch((error) => {
        console.error(
          "Service worker not registered or failed to become ready:",
          error
        );

        // Set user context for error reporting
        if (auth?.userId || auth?.tenant) {
          Sentry.setUser({
            id: auth?.userId || "",
            email: auth?.userName || "",
            username: auth.userName || "",
          });

          Sentry.setContext("user_info", {
            userId: auth.userId,
            tenant: auth.tenant,
            username: auth.userName,
          });

          Sentry.setTag("tenant", auth.tenant || "");
          Sentry.setTag("userId", auth.userId || "");
        }

        // Capture the service worker error
        Sentry.captureException(error, {
          tags: {
            source: "service_worker_ready",
            userId: auth?.userId || "",
            tenant: auth?.tenant || "",
          },
          contexts: {
            service_worker: {
              action: "ready_check",
              timestamp: new Date().toISOString(),
              userAgent: navigator.userAgent,
            },
          },
        });
      });

    function handleOnline() {
      console.log("Online");

      // Set network status context
      Sentry.setContext("network_status", {
        status: "online",
        timestamp: new Date().toISOString(),
        id: auth?.userId,
        tenant: auth?.tenant,
        username: auth.userName,
      });

      // Add breadcrumb for network status change
      Sentry.addBreadcrumb({
        message: "Network status changed to online",
        category: "network",
        level: "info",
        data: {
          status: "online",
          userId: auth?.userId || "anonymous",
          tenant: auth?.tenant || "unknown",
          timestamp: new Date().toISOString(),
        },
      });

      // Set tag for current network status
      Sentry.setTag("networkStatus", "online");
    }

    function handleOffline() {
      console.log("Offline");

      // Automatically enable offline mode when internet is lost
      if (!isOfflineMode()) {
        turnOfflineModeOn();
      }

      // Set network status context
      Sentry.setContext("network_status", {
        status: "offline",
        timestamp: new Date().toISOString(),
        id: auth?.userId,
        tenant: auth?.tenant,
        username: auth.userName,
      });

      // Add breadcrumb for network status change
      Sentry.addBreadcrumb({
        message: "Network status changed to offline",
        category: "network",
        level: "warning",
        data: {
          status: "offline",
          userId: auth?.userId || "anonymous",
          tenant: auth?.tenant || "unknown",
          timestamp: new Date().toISOString(),
        },
      });

      // Set tag for current network status
      Sentry.setTag("networkStatus", "offline");

      // Optionally capture offline events as messages (not errors)
      Sentry.captureMessage("Application went offline", {
        level: "warning", // Specify the level explicitly
        tags: {
          source: "network_status",
          userId: auth?.userId ?? "", // Use nullish coalescing for safer access
          tenant: auth?.tenant ?? "",
        },
        contexts: {
          network_event: {
            status: "offline",
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
          },
        },
      });
    }

    // Set initial network status
    const initialNetworkStatus = navigator.onLine ? "online" : "offline";
    Sentry.setTag("networkStatus", initialNetworkStatus);
    Sentry.setContext("network_status", {
      status: initialNetworkStatus,
      timestamp: new Date().toISOString(),
      id: auth?.userId,
      tenant: auth?.tenant,
      username: auth.userName,
    });

    // iOS has no Background Sync, so queued writes used to drain only at app
    // launch. Ask the worker to flush whenever the app returns to the foreground.
    function handleVisible() {
      if (document.visibilityState !== "visible" || !navigator.onLine) return;
      postMessage({
        type: "SYNC_QUEUE",
        payload: { message: "Sync queue after returning to foreground" },
      }).catch((error) =>
        console.error("Foreground sync request failed:", error)
      );
    }

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", handleVisible);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", handleVisible);
    };
  }, [auth]);

  // Monitor route changes for better error context
  useEffect(() => {
    const currentPath = window.location.pathname;

    // Add breadcrumb for route change
    Sentry.addBreadcrumb({
      message: `Route changed to ${currentPath}`,
      category: "navigation",
      level: "info",
      data: {
        from: document.referrer || "direct",
        to: currentPath,
        timestamp: new Date().toISOString(),
      },
    });

    // Set current route as tag
    Sentry.setTag("currentRoute", currentPath);
  }, [window.location.pathname]);

  return (
    <>
      <AppRoutes />
      {message && type && (
        <Toaster message={message} type={type} onClose={handleToasterClose} />
      )}
      <ToastContainer />
    </>
  );
};

// Wrap the App component with Sentry for additional React-specific error tracking
export default App;
